import { alignDecorative, isDecorative, packDecorative } from "./decorative-segments.ts";
import { alignLineStyles, parseStyleDefaults } from "./feature-style.ts";
import type { LinePattern, LineStyle } from "./feature-style.ts";
import { regionFeatures, type RegionFeature } from "./region-fill.ts";
import type { LngLat, PieceGraph } from "./types.ts";

/** One drawn segment with the stroke saved from the route editor. */
export interface RoutePaintFeature {
  type: "Feature";
  properties: { stroke: string; width: number; pattern: LinePattern; decorative?: boolean };
  geometry: { type: "LineString"; coordinates: LngLat[] };
}

/** Closed region fill copied from the route editor (`regions` on the piece graph). */
export type RouteFillFeature = RegionFeature;

export const DEFAULT_QUEST_ROUTE_COLOR = "#8b5cf6";
export const DEFAULT_QUEST_ROUTE_WIDTH = 4;

/** Build display features from the editor graph at bind time (one feature per segment). */
export function routeFillFromGraph(graph: PieceGraph): RouteFillFeature[] {
  const regions = graph.regions;
  if (!regions?.length || graph.points.length === 0) return [];
  return regionFeatures(regions, graph.points).features;
}

export function encodeRouteFillPaint(features: readonly RouteFillFeature[]): string | null {
  if (features.length === 0) return null;
  return JSON.stringify(features);
}

export function decodeRouteFillPaint(text: string | null | undefined): RouteFillFeature[] | null {
  if (!text) return null;
  try {
    const raw = JSON.parse(text) as unknown;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const out: RouteFillFeature[] = [];
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const props = (item as { properties?: unknown }).properties as { color?: unknown } | undefined;
      const geom = (item as { geometry?: { type?: string; coordinates?: unknown } }).geometry;
      if (
        !props ||
        typeof props.color !== "string" ||
        geom?.type !== "Polygon" ||
        !Array.isArray(geom.coordinates)
      ) {
        continue;
      }
      out.push({
        type: "Feature",
        properties: { color: props.color },
        geometry: { type: "Polygon", coordinates: geom.coordinates as LngLat[][] },
      });
    }
    return out.length > 0 ? out : null;
  } catch {
    return null;
  }
}

export function routeFillFeatureCollection(
  fill: readonly RouteFillFeature[] | null,
): { type: "FeatureCollection"; features: RouteFillFeature[] } {
  return { type: "FeatureCollection", features: fill && fill.length > 0 ? [...fill] : [] };
}

export function routePaintFromGraph(graph: PieceGraph): RoutePaintFeature[] {
  const defaults = parseStyleDefaults(graph.styleDefaults);
  const lineStyles = alignLineStyles(graph.lineStyles, graph.segments.length);
  const decorative = alignDecorative(graph.decorative, graph.segments.length);
  const features: RoutePaintFeature[] = [];
  for (let i = 0; i < graph.segments.length; i++) {
    const seg = graph.segments[i];
    if (!seg) continue;
    const [a, b] = seg;
    const pa = graph.points[a];
    const pb = graph.points[b];
    if (!pa || !pb) continue;
    const style = lineStyles[i] ?? defaults.line;
    const props: RoutePaintFeature["properties"] = {
      stroke: style.color,
      width: style.width,
      pattern: style.pattern,
    };
    if (isDecorative(decorative, i)) props.decorative = true;
    features.push({
      type: "Feature",
      properties: props,
      geometry: { type: "LineString", coordinates: [pa, pb] },
    });
  }
  return features;
}

export function encodeRouteLinePaint(features: readonly RoutePaintFeature[]): string | null {
  if (features.length === 0) return null;
  return JSON.stringify(features);
}

export function decodeRouteLinePaint(text: string | null | undefined): RoutePaintFeature[] | null {
  if (!text) return null;
  try {
    const raw = JSON.parse(text) as unknown;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const out: RoutePaintFeature[] = [];
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const props = (item as { properties?: unknown }).properties as
        | { stroke?: unknown; width?: unknown; pattern?: unknown; decorative?: unknown }
        | undefined;
      const geom = (item as { geometry?: { type?: string; coordinates?: unknown } }).geometry;
      if (
        !props ||
        typeof props.stroke !== "string" ||
        typeof props.width !== "number" ||
        (props.pattern !== "solid" && props.pattern !== "dashed" && props.pattern !== "dotted") ||
        geom?.type !== "LineString" ||
        !Array.isArray(geom.coordinates) ||
        geom.coordinates.length < 2
      ) {
        continue;
      }
      const featureProps: RoutePaintFeature["properties"] = {
        stroke: props.stroke,
        width: props.width,
        pattern: props.pattern,
      };
      if (props.decorative === true) featureProps.decorative = true;
      out.push({
        type: "Feature",
        properties: featureProps,
        geometry: { type: "LineString", coordinates: geom.coordinates as LngLat[] },
      });
    }
    return out.length > 0 ? out : null;
  } catch {
    return null;
  }
}

/** Fallback when no paint was stored (older quests). */
export function routePaintFromCoordinates(
  coordinates: LngLat[],
  opts: { color?: string; width?: number } = {},
): RoutePaintFeature[] {
  if (coordinates.length < 2) return [];
  return [
    {
      type: "Feature",
      properties: {
        stroke: opts.color ?? DEFAULT_QUEST_ROUTE_COLOR,
        width: opts.width ?? DEFAULT_QUEST_ROUTE_WIDTH,
        pattern: "solid",
      },
      geometry: { type: "LineString", coordinates },
    },
  ];
}

const SEG_MATCH_EPS = 1e-5;

/** Quantized endpoint pair. Identical copies share a key; order does not matter. */
function segmentKey(a: LngLat, b: LngLat): string {
  const ax = Math.round(a[0] / SEG_MATCH_EPS);
  const ay = Math.round(a[1] / SEG_MATCH_EPS);
  const bx = Math.round(b[0] / SEG_MATCH_EPS);
  const by = Math.round(b[1] / SEG_MATCH_EPS);
  const aFirst = ax < bx || (ax === bx && ay <= by);
  return aFirst ? `${ax}:${ay}|${bx}:${by}` : `${bx}:${by}|${ax}:${ay}`;
}

/** First feature for each endpoint pair. Lookup is O(1); the old scan was O(segments × paint). */
function paintByEndpoints(paint: readonly RoutePaintFeature[]): Map<string, RoutePaintFeature> {
  const index = new Map<string, RoutePaintFeature>();
  for (const feature of paint) {
    const coords = feature.geometry.coordinates;
    if (coords.length < 2) continue;
    const key = segmentKey(coords[0]!, coords[coords.length - 1]!);
    if (!index.has(key)) index.set(key, feature);
  }
  return index;
}

function featureForSegment(
  index: Map<string, RoutePaintFeature>,
  pa: LngLat,
  pb: LngLat,
): RoutePaintFeature | undefined {
  return index.get(segmentKey(pa, pb));
}

/** Map saved segment paint onto a chain graph built from the quest route line. */
export function lineStylesFromRoutePaint(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
  paint: readonly RoutePaintFeature[] | null | undefined,
): LineStyle[] | undefined {
  if (!paint?.length || segments.length === 0) return undefined;
  const styles: LineStyle[] = segments.map(() => ({
    color: parseStyleDefaults(undefined).line.color,
    width: parseStyleDefaults(undefined).line.width,
    pattern: "solid" as LinePattern,
  }));
  const index = paintByEndpoints(paint);
  let matched = 0;
  for (let i = 0; i < segments.length; i++) {
    const [ai, bi] = segments[i]!;
    const pa = points[ai];
    const pb = points[bi];
    if (!pa || !pb) continue;
    const feature = featureForSegment(index, pa, pb);
    if (!feature) continue;
    matched++;
    styles[i] = {
      color: feature.properties.stroke,
      width: feature.properties.width,
      pattern: feature.properties.pattern,
    };
  }
  return matched > 0 ? styles : undefined;
}

/** Map saved segment paint onto a chain graph's decorative flags. */
export function decorativeFromRoutePaint(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
  paint: readonly RoutePaintFeature[] | null | undefined,
): boolean[] | undefined {
  if (!paint?.length || segments.length === 0) return undefined;
  const flags = alignDecorative(undefined, segments.length);
  const index = paintByEndpoints(paint);
  let matched = 0;
  for (let i = 0; i < segments.length; i++) {
    const [ai, bi] = segments[i]!;
    const pa = points[ai];
    const pb = points[bi];
    if (!pa || !pb) continue;
    const feature = featureForSegment(index, pa, pb);
    if (!feature?.properties.decorative) continue;
    matched++;
    flags[i] = true;
  }
  return packDecorative(flags, segments.length);
}

/** Route line strokes shown on maps outside the editor (decorative 장식선 excluded). */
export function routePaintForMapView(
  paint: readonly RoutePaintFeature[] | null,
): readonly RoutePaintFeature[] | null {
  if (!paint?.length) return paint;
  return paint.filter((f) => !f.properties.decorative);
}

/** Every vertex used to draw the route (line, shown styled segments, and region fills). */
export function routeDisplayFitPoints(
  line: readonly LngLat[] | null,
  paint: readonly RoutePaintFeature[] | null,
  fill: readonly RouteFillFeature[] | null,
): LngLat[] {
  const out: LngLat[] = [];
  const push = (p: LngLat) => {
    if (Number.isFinite(p[0]) && Number.isFinite(p[1])) out.push(p);
  };
  if (line) for (const p of line) push(p);
  for (const f of routePaintForMapView(paint) ?? []) {
    for (const p of f.geometry.coordinates) push(p);
  }
  if (fill) {
    for (const f of fill) {
      for (const ring of f.geometry.coordinates) {
        for (const p of ring) push(p);
      }
    }
  }
  return out;
}

export type RouteCanvasFitBounds = {
  west: number;
  south: number;
  east: number;
  north: number;
};

/**
 * Home framing uses the geometry that is actually drawn (line, shown paint, fill).
 * The editor canvas is the artboard around that art, so its corners are not part of the fit.
 */
export function routeHomeMapFitPoints(
  line: readonly LngLat[] | null,
  paint: readonly RoutePaintFeature[] | null,
  fill: readonly RouteFillFeature[] | null,
  _canvas?: RouteCanvasFitBounds | null,
): LngLat[] {
  return routeDisplayFitPoints(line, paint, fill);
}

type RoutePaintMapFeature = {
  type: "Feature";
  properties: { stroke: string; width: number; pattern: LinePattern };
  geometry: RoutePaintFeature["geometry"];
};

/** Map views never draw decorative strokes, so their GeoJSON drops the flag entirely. */
function paintFeatureForMap(feature: RoutePaintFeature): RoutePaintMapFeature {
  const { decorative: _decorative, width, stroke, pattern, ...rest } = feature.properties;
  return {
    type: "Feature",
    properties: {
      ...rest,
      stroke: typeof stroke === "string" ? stroke : DEFAULT_QUEST_ROUTE_COLOR,
      width: typeof width === "number" && Number.isFinite(width) ? width : DEFAULT_QUEST_ROUTE_WIDTH,
      pattern: pattern === "dashed" || pattern === "dotted" ? pattern : "solid",
    },
    geometry: feature.geometry,
  };
}

export function routePaintFeatureCollection(
  paint: readonly RoutePaintFeature[] | null,
  coordinates: LngLat[] | null,
  fallbackColor = DEFAULT_QUEST_ROUTE_COLOR,
): { type: "FeatureCollection"; features: RoutePaintMapFeature[] } {
  const viewPaint = routePaintForMapView(paint);
  const features =
    viewPaint && viewPaint.length > 0
      ? viewPaint.map((feature) => paintFeatureForMap(feature))
      : coordinates && coordinates.length >= 2
        ? routePaintFromCoordinates(coordinates, { color: fallbackColor }).map((feature) =>
            paintFeatureForMap(feature),
          )
        : [];
  return { type: "FeatureCollection", features };
}
